import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PlusIcon, XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';

import api from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const AddProduct = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    defaultValues: {
      currency: 'SDG',
      inventoryTrackQuantity: true,
      shippingFreeShipping: false,
      status: 'draft',
      inventoryQuantity: 0,
      inventorySku: '',
      inventoryLowStockThreshold: 5,
      shippingCost: 50
    }
  });

  // جلب الفئات
  const { data: categories, isLoading: categoriesLoading } = useQuery(
    'categories',
    () => api.get('/categories').then(res => res.data.data)
  );

  // إضافة منتج
  const addProductMutation = useMutation(
    (productData) => api.post('/products', productData),
    {
      onSuccess: (response) => {
        toast.success('تم إضافة المنتج بنجاح!');
        navigate('/vendor/products');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'حدث خطأ في إضافة المنتج';
        toast.error(message);
      }
    }
  );

  // معالجة رفع الصور
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length + images.length > 5) {
      toast.error('يمكنك رفع 5 صور كحد أقصى');
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          file,
          preview: e.target.result,
          id: Date.now() + Math.random()
        };
        
        setImages(prev => [...prev, newImage]);
        setImagePreview(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  // حذف صورة
  const removeImage = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
    setImagePreview(prev => {
      const imageIndex = images.findIndex(img => img.id === imageId);
      return prev.filter((_, index) => index !== imageIndex);
    });
  };

  // إرسال النموذج
  const onSubmit = async (data) => {
    if (images.length === 0) {
      toast.error('يجب إضافة صورة واحدة على الأقل');
      return;
    }

    // Get values with new field names
    const quantityValue = data.inventoryQuantity ? parseInt(data.inventoryQuantity, 10) : 0;
    const skuValue = data.inventorySku ? String(data.inventorySku).trim() : '';

    console.log('=== DEBUG: Form Data ===');
    console.log('All form data:', data);
    console.log('inventoryQuantity from form:', data.inventoryQuantity);
    console.log('Parsed quantity:', quantityValue);
    console.log('Type:', typeof quantityValue);

    // تحضير بيانات المنتج بالبنية التي يتوقعها Laravel
    const productData = {
      name: data.name,
      description: data.description,
      category: data.category,
      price: Number(data.price),
      comparePrice: data.comparePrice ? Number(data.comparePrice) : null,
      status: data.status || 'draft',
      inventory: {
        sku: skuValue || null,
        quantity: quantityValue,
        trackQuantity: data.inventoryTrackQuantity !== false,
        allowBackorder: data.inventoryAllowBackorder || false,
        lowStockThreshold: Number(data.inventoryLowStockThreshold) || 5
      },
      shipping: {
        freeShipping: data.shippingFreeShipping || false,
        shippingCost: data.shippingFreeShipping ? 0 : Number(data.shippingCost) || 50,
        processingTime: data.shippingProcessingTime || '1-2 أيام عمل'
      },
      images: imagePreview.map((preview, index) => ({
        url: preview,
        alt: data.name,
        isMain: index === 0
      }))
    };

    console.log('=== DEBUG: Product Data to Send ===');
    console.log('Full product data:', productData);
    console.log('Inventory object:', productData.inventory);
    console.log('Quantity in inventory:', productData.inventory.quantity);

    addProductMutation.mutate(productData);
  };

  if (categoriesLoading) {
    return (
      <VendorLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">إضافة منتج جديد</h1>
            <button
              type="button"
              onClick={() => navigate('/vendor/products')}
              className="text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* معلومات أساسية */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">اسم المنتج *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="أدخل اسم المنتج"
                  {...register('name', { 
                    required: 'اسم المنتج مطلوب',
                    minLength: { value: 2, message: 'اسم المنتج يجب أن يكون حرفين على الأقل' }
                  })}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
              </div>

              <div>
                <label className="form-label">الفئة *</label>
                <select
                  className="form-input"
                  {...register('category', { required: 'الفئة مطلوبة' })}
                >
                  <option value="">اختر الفئة</option>
                  {categories?.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>}
              </div>
            </div>

            {/* الوصف */}
            <div>
              <label className="form-label">وصف المنتج *</label>
              <textarea
                rows={4}
                className="form-input"
                placeholder="اكتب وصفاً مفصلاً للمنتج"
                {...register('description', { 
                  required: 'وصف المنتج مطلوب',
                  minLength: { value: 10, message: 'الوصف يجب أن يكون 10 أحرف على الأقل' }
                })}
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>}
            </div>

            {/* الأسعار */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="form-label">السعر (جنيه سوداني) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-input"
                  placeholder="0.00"
                  {...register('price', { 
                    required: 'السعر مطلوب',
                    min: { value: 0, message: 'السعر لا يمكن أن يكون سالباً' }
                  })}
                />
                {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price.message}</p>}
              </div>

              <div>
                <label className="form-label">السعر المقارن (اختياري)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-input"
                  placeholder="0.00"
                  {...register('comparePrice')}
                />
              </div>

              <div>
                <label className="form-label">العملة</label>
                <select className="form-input" {...register('currency')}>
                  <option value="SDG">جنيه سوداني (SDG)</option>
                  <option value="USD">دولار أمريكي (USD)</option>
                </select>
              </div>
            </div>

            {/* المخزون */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-4">إدارة المخزون</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">رمز المنتج (SKU)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="مثال: PROD-001 (اختياري)"
                    {...register('inventorySku')}
                  />
                </div>

                <div>
                  <label className="form-label">الكمية *</label>
                  <input
                    type="number"
                    min="0"
                    className="form-input"
                    placeholder="0"
                    defaultValue={0}
                    {...register('inventoryQuantity', { 
                      required: 'الكمية مطلوبة',
                      valueAsNumber: true,
                      min: { value: 0, message: 'الكمية لا يمكن أن تكون سالبة' }
                    })}
                  />
                  {errors.inventoryQuantity && <p className="text-red-500 text-sm mt-1">{errors.inventoryQuantity.message}</p>}
                </div>
              </div>

              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    {...register('inventoryTrackQuantity')}
                  />
                  <span className="mr-2 text-sm text-gray-700">تتبع كمية المخزون</span>
                </label>
              </div>
            </div>

            {/* الصور */}
            <div>
              <label className="form-label">صور المنتج *</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <label htmlFor="images" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        اضغط لرفع الصور أو اسحبها هنا
                      </span>
                      <input
                        id="images"
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      PNG, JPG, GIF حتى 5MB (5 صور كحد أقصى)
                    </p>
                  </div>
                </div>

                {/* معاينة الصور */}
                {imagePreview.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
                    {imagePreview.map((preview, index) => (
                      <div key={images[index]?.id} className="relative">
                        <img
                          src={preview}
                          alt={`معاينة ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(images[index]?.id)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                        {index === 0 && (
                          <span className="absolute bottom-1 left-1 bg-primary-600 text-white text-xs px-2 py-1 rounded">
                            رئيسية
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* الشحن */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-4">إعدادات الشحن</h3>
              
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    {...register('shippingFreeShipping')}
                  />
                  <span className="mr-2 text-sm text-gray-700">شحن مجاني</span>
                </label>

                {!watch('shippingFreeShipping') && (
                  <div>
                    <label className="form-label">تكلفة الشحن (جنيه)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-input"
                      placeholder="50.00"
                      {...register('shippingCost')}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      اتركه فارغاً أو 0 لاستخدام رسوم الشحن الافتراضية (50 جنيه)
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* أزرار الحفظ */}
            <div className="flex justify-end space-x-4 space-x-reverse">
              <button
                type="button"
                onClick={() => navigate('/vendor/products')}
                className="btn-outline"
                disabled={addProductMutation.isLoading}
              >
                إلغاء
              </button>
              
              <button
                type="submit"
                onClick={() => setValue('status', 'draft')}
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg"
                disabled={addProductMutation.isLoading}
              >
                حفظ كمسودة
              </button>
              
              <button
                type="submit"
                onClick={() => setValue('status', 'active')}
                className="btn-primary"
                disabled={addProductMutation.isLoading}
              >
                {addProductMutation.isLoading ? 'جاري النشر...' : 'نشر المنتج'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </VendorLayout>
  );
};

export default AddProduct;